/**
 * parseoListaRaya.js
 *
 * Única fuente de verdad del layout del Excel "Lista de Raya (forma tabular)" que
 * exporta CONTPAQi, usado para cargar nóminas históricas (ver
 * erp_backend/Controllers/Prenomina/historica.controllers.js).
 *
 * Módulo PURO: no importa React, ni axios, ni modelos. Se puede correr con Node
 * contra un archivo real transpilando el ESM a CJS con un reemplazo textual —
 * mismo método que se usa para mapeoExterno.js.
 *
 * El archivo NO es una matriz limpia. Lo que hay que resolver:
 *
 *  - Ni el renglón de encabezado ni la columna inicial son fijos. En un mismo
 *    libro conviven hojas con el encabezado en el renglón 6 arrancando en la
 *    columna C, y hojas con el encabezado en el 7 arrancando en la A. Se detecta
 *    buscando el renglón que contiene "Código" y "Empleado".
 *  - Entre los renglones de empleado hay cabeceras de agrupación
 *    ("Reg. Pat. IMSS: …", "Departamento 12 ISUZU") y subtotales ("Total Depto",
 *    "Total Gral.") seguidos de un renglón numérico sin código. Un renglón es de
 *    empleado únicamente cuando su celda "Código" es numérica.
 *  - Un mismo nombre de columna puede aparecer dos veces con significados
 *    distintos: "Préstamo empresa" existe como percepción y como deducción. Por
 *    eso el mapeo se lleva SIEMPRE por índice de columna, nunca por nombre, y el
 *    tipo se deduce de si la columna cae antes o después de
 *    "*TOTAL* *PERCEPCIONES*".
 *  - Las columnas de totales de control (*TOTAL* *PERCEPCIONES*,
 *    *TOTAL* *DEDUCCIONES*, *NETO*) no son conceptos: sirven para validar que la
 *    suma de lo mapeado cuadre con lo que pagó el sistema anterior.
 *  - "BBVA", "BAJIO" y similares son números de cuenta bancaria, no importes.
 */

import { normalizarTexto } from '../../Empleados/ImportarEmpleadosExterno/mapeoExterno'

// ─── Constantes del layout ───────────────────────────────────────────────────

export const COL_CODIGO   = 'codigo'
export const COL_EMPLEADO = 'empleado'

// Columnas de control: delimitan percepciones/deducciones y sirven para validar.
export const COL_TOTAL_PERCEPCIONES = 'total percepciones'
export const COL_TOTAL_DEDUCCIONES  = 'total deducciones'
export const COL_NETO               = 'neto'

// Encabezados que nunca son un concepto de nómina.
const COLUMNAS_IGNORADAS_POR_DEFECTO = new Set([
  'bbva', 'bajio', 'banorte', 'santander', 'banamex', 'hsbc', 'scotiabank',
  'banco', 'cuenta', 'clabe', 'observaciones', 'departamento', 'rfc', 'curp',
  'nss', 'puesto',
])

// Prefijos de renglón que son cabecera de agrupación o subtotal, no un empleado.
const RENGLONES_NO_EMPLEADO = [
  /^reg\.?\s*pat/,      // "Reg. Pat. IMSS:  Y7324836100"
  /^departamento\b/,    // "Departamento 12 ISUZU"
  /^total\b/,           // "Total Depto", "Total Gral."
  /^codigo$/,           // el propio encabezado
]

export const TOLERANCIA_CENTAVOS = 0.011

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * CONTPAQi decora los encabezados de control con asteriscos ("*TOTAL*
 * *PERCEPCIONES*"). Se quitan para poder compararlos, pero el encabezado crudo se
 * conserva aparte porque es la llave con la que se recuerda el mapeo.
 */
export function normalizarEncabezado(valor) {
  return normalizarTexto(String(valor ?? '').replace(/\*/g, ' '))
}

export function parsearNumero(valor) {
  if (valor === null || valor === undefined || valor === '') return null
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : null
  // Los subtotales traen "  -----------------------" en vez de un número.
  const limpio = String(valor).replace(/[\s,$]/g, '')
  if (!limpio || /^-+$/.test(limpio)) return null
  const n = Number(limpio)
  return Number.isFinite(n) ? n : null
}

const redondear2 = (n) => Math.round((Number(n) || 0) * 100) / 100

// ─── Detección del encabezado ────────────────────────────────────────────────

/**
 * Busca el renglón que contiene a la vez "Código" y "Empleado".
 * @returns {{ renglon:number, colCodigo:number, colEmpleado:number, encabezados:string[] }|null}
 */
export function detectarEncabezado(filas) {
  const limite = Math.min(filas.length, 40)
  for (let i = 0; i < limite; i++) {
    const fila = filas[i] || []
    let colCodigo = -1
    let colEmpleado = -1
    for (let j = 0; j < fila.length; j++) {
      const h = normalizarEncabezado(fila[j])
      if (colCodigo === -1 && h === COL_CODIGO) colCodigo = j
      if (colEmpleado === -1 && h === COL_EMPLEADO) colEmpleado = j
    }
    if (colCodigo !== -1 && colEmpleado !== -1 && colEmpleado > colCodigo) {
      return {
        renglon: i,
        colCodigo,
        colEmpleado,
        encabezados: fila.map((h) => String(h ?? '').trim()),
      }
    }
  }
  return null
}

/**
 * Metadatos de la cabecera de CONTPAQi (los renglones sobre el encabezado). Solo
 * informativo: sirve para que la pantalla muestre de qué periodo y empresa es el
 * archivo y el usuario confirme que eligió el Periodo correcto.
 */
export function leerCabecera(filas, renglonEncabezado) {
  const texto = []
  for (let i = 0; i < renglonEncabezado; i++) {
    for (const celda of filas[i] || []) {
      const s = String(celda ?? '').trim()
      if (s) texto.push(s)
    }
  }
  const buscar = (re) => {
    for (const t of texto) {
      const m = t.match(re)
      if (m) return m
    }
    return null
  }
  const periodo = buscar(/Periodo\s+(\d+)\s+al\s+(\d+)\s+(\S+)\s+del\s+(\S+)\s+a[l]?\s+(\S+)/i)
  const rfc = buscar(/RFC:\s*([A-Za-z0-9\-&\s]+)/)
  return {
    empresa:      texto.find((t) => /^[A-ZÑÁÉÍÓÚ0-9][A-ZÑÁÉÍÓÚ0-9 .,&]{8,}$/.test(t) && !/CONTPAQ|NOMINAS/i.test(t)) || '',
    periodoTexto: periodo ? periodo[0] : '',
    periodoNumero: periodo ? Number(periodo[1]) : null,
    fechaInicio:  periodo ? periodo[4] : '',
    fechaFin:     periodo ? periodo[5] : '',
    rfc:          rfc ? rfc[1].replace(/[\s-]/g, '').trim() : '',
  }
}

// ─── Columnas ────────────────────────────────────────────────────────────────

/**
 * Clasifica las columnas a la derecha de "Empleado".
 *
 * `tipo` = 1 (percepción) para las que están antes de *TOTAL* *PERCEPCIONES*,
 * 2 (deducción) para las de después. Ese corte es la única manera confiable de
 * distinguir el "Préstamo empresa" que se entrega del que se descuenta.
 *
 * @returns {Array<{indice, encabezado, clave, tipo, control, ignorarPorDefecto}>}
 */
export function columnasDeConceptos({ encabezados, colEmpleado }) {
  const columnas = []
  let tipoActual = 1
  let vistoTotalPercepciones = false

  for (let j = colEmpleado + 1; j < encabezados.length; j++) {
    const encabezado = String(encabezados[j] ?? '').trim()
    if (!encabezado) continue
    const clave = normalizarEncabezado(encabezado)

    let control = null
    if (clave === COL_TOTAL_PERCEPCIONES) control = 'totalPercepciones'
    else if (clave === COL_TOTAL_DEDUCCIONES) control = 'totalDeducciones'
    else if (clave === COL_NETO) control = 'neto'

    columnas.push({
      indice: j,
      encabezado,
      clave,
      tipo: control ? null : tipoActual,
      control,
      ignorarPorDefecto: !!control || COLUMNAS_IGNORADAS_POR_DEFECTO.has(clave),
    })

    if (control === 'totalPercepciones' && !vistoTotalPercepciones) {
      vistoTotalPercepciones = true
      tipoActual = 2
    }
  }

  return columnas
}

/**
 * Propone un ConceptoNomina para cada columna comparando la descripción
 * normalizada, exigiendo además que coincida el tipo (percepción/deducción).
 * No adivina por parecido: si no hay coincidencia exacta, la columna queda sin
 * mapear y el usuario la resuelve a mano. Mandar un importe al concepto
 * equivocado desacomoda los acumulados del ejercicio entero.
 */
export function automapear(columnas, conceptosNomina, mapeoGuardado = []) {
  // La llave es encabezado + tipo, no solo el encabezado: "Préstamo empresa" existe
  // como percepción y como deducción, y con una llave por nombre el segundo pisaría
  // al primero y ambas columnas se restaurarían al mismo concepto.
  const llaveGuardado = (columna, tipo) => `${normalizarEncabezado(columna)}|${tipo ?? ''}`
  const guardadoPorColumna = new Map(
    (mapeoGuardado || [])
      .filter((m) => m && m.columna)
      .map((m) => [llaveGuardado(m.columna, m.tipo), m])
  )

  // Un mismo texto puede existir como percepción y como deducción en el catálogo.
  const porClaveYTipo = new Map()
  for (const c of conceptosNomina || []) {
    const k = `${normalizarTexto(c.descripcion)}|${c.tipo}`
    if (!porClaveYTipo.has(k)) porClaveYTipo.set(k, c)
  }

  return columnas.map((col) => {
    const guardado = guardadoPorColumna.get(llaveGuardado(col.encabezado, col.tipo))
    if (guardado) {
      if (guardado.ignorar) return { ...col, conceptoNominaId: null, ignorar: true, origen: 'guardado' }
      const existe = (conceptosNomina || []).some(
        (c) => String(c._id) === String(guardado.conceptoNominaId)
      )
      if (existe) {
        return {
          ...col,
          conceptoNominaId: String(guardado.conceptoNominaId),
          ignorar: false,
          origen: 'guardado',
        }
      }
    }

    if (col.ignorarPorDefecto) {
      return { ...col, conceptoNominaId: null, ignorar: true, origen: 'automatico' }
    }

    const encontrado = porClaveYTipo.get(`${col.clave}|${col.tipo}`)
    return {
      ...col,
      conceptoNominaId: encontrado ? String(encontrado._id) : null,
      ignorar: false,
      origen: encontrado ? 'automatico' : null,
    }
  })
}

// ─── Renglones ───────────────────────────────────────────────────────────────

export function esRenglonEmpleado(fila, colCodigo) {
  const cruda = String(fila?.[colCodigo] ?? '').trim()
  if (!cruda) return false
  const normal = normalizarTexto(cruda)
  if (RENGLONES_NO_EMPLEADO.some((re) => re.test(normal))) return false
  return /^\d+$/.test(cruda)
}

/**
 * Extrae los renglones de empleado con el importe de cada columna y los totales
 * de control del propio archivo.
 */
export function extraerRenglones(filas, encabezado) {
  const { renglon, colCodigo, colEmpleado } = encabezado
  const columnas = columnasDeConceptos(encabezado)
  const control = {
    totalPercepciones: columnas.find((c) => c.control === 'totalPercepciones')?.indice ?? null,
    totalDeducciones:  columnas.find((c) => c.control === 'totalDeducciones')?.indice ?? null,
    neto:              columnas.find((c) => c.control === 'neto')?.indice ?? null,
  }

  const renglones = []
  let departamento = ''

  for (let i = renglon + 1; i < filas.length; i++) {
    const fila = filas[i] || []
    const primera = normalizarTexto(fila[colCodigo] ?? '')

    if (/^departamento\b/.test(primera)) {
      departamento = String(fila[colCodigo]).trim()
      continue
    }
    if (!esRenglonEmpleado(fila, colCodigo)) continue

    const importes = {}
    for (const col of columnas) {
      if (col.control) continue
      const n = parsearNumero(fila[col.indice])
      if (n !== null && n !== 0) importes[col.indice] = redondear2(n)
    }

    renglones.push({
      filaExcel: i + 1, // 1-based, como lo ve el usuario en Excel
      departamento,
      codigo: String(fila[colCodigo]).trim(),
      nombreArchivo: String(fila[colEmpleado] ?? '').trim(),
      importes,
      totalPercepcionesArchivo: control.totalPercepciones !== null ? parsearNumero(fila[control.totalPercepciones]) : null,
      totalDeduccionesArchivo:  control.totalDeducciones  !== null ? parsearNumero(fila[control.totalDeducciones])  : null,
      netoArchivo:              control.neto              !== null ? parsearNumero(fila[control.neto])              : null,
    })
  }

  return { columnas, renglones }
}

// ─── Emparejamiento con los empleados del sistema ────────────────────────────

/**
 * `Empleado.codigo` es el mismo "Código" del sistema externo — lo llena
 * mapeoExterno.js al importar empleados. Se empareja por ahí y, si no aparece,
 * por nombre completo normalizado.
 *
 * El código solo es único entre los ACTIVOS: un mismo código puede traer al
 * habilitado y a uno o más deshabilitados (un reingreso dado de alta como empleado
 * nuevo, un duplicado unificado...). Por eso el índice guarda una LISTA por código,
 * y con varios candidatos el renglón deja elegir (ver emparejarRenglon).
 *
 * El nombre del archivo viene como "APELLIDO PATERNO APELLIDO MATERNO NOMBRES"
 * sin separador, así que no se puede partir en tres. Se compara el conjunto de
 * palabras: mismo multiconjunto de palabras = misma persona, sin importar el orden.
 */
const clavePalabras = (texto) =>
  normalizarTexto(texto).split(' ').filter(Boolean).sort().join(' ')

const agregarALista = (mapa, llave, valor) => {
  if (!mapa.has(llave)) mapa.set(llave, [])
  mapa.get(llave).push(valor)
}

export function construirIndicesEmpleadosNomina(empleados = []) {
  const porCodigo = new Map()
  const porPalabras = new Map()
  for (const emp of empleados) {
    if (emp.codigo !== null && emp.codigo !== undefined && emp.codigo !== '') {
      agregarALista(porCodigo, String(Number(emp.codigo)), emp)
    }
    const llave = clavePalabras(
      [emp.apellidoPaterno, emp.apellidoMaterno, emp.nombre].filter(Boolean).join(' ')
    )
    if (llave) agregarALista(porPalabras, llave, emp)
  }
  return { porCodigo, porPalabras }
}

const nombreEmpleado = (e) =>
  [e.nombre, e.apellidoPaterno, e.apellidoMaterno].filter(Boolean).join(' ')

// Lo que necesita el selector de la pantalla para cada candidato.
const resumenCandidato = (e) => ({
  _id: String(e._id),
  nombre: nombreEmpleado(e),
  codigo: e.codigo,
  serieNumero: e.serieNumero,
  rfc: e.rfc,
  estado: e.estado,
})

/**
 * Empareja un renglón con un empleado del sistema.
 *
 * Con VARIOS candidatos (mismo código, o sin código y mismo nombre):
 *   1. si el usuario eligió uno (`elegidoId`) y sigue siendo candidato, ese;
 *   2. si no, el único HABILITADO (estado 1) que no esté tomado por otro renglón;
 *   3. si no hay un habilitado único, el renglón queda pendiente de elegir.
 *
 * @param usados      ids ya emparejados por renglones anteriores
 * @param elegidoId   elección manual del usuario para este renglón
 * @param reservados  Map empleadoId → filaExcel con las elecciones manuales de
 *                    TODOS los renglones: el habilitado que se propone por defecto
 *                    en un renglón no le gana al que el usuario eligió en otro.
 * @returns {{ empleado, criterio, error, candidatos, eleccion }}
 *   `candidatos` solo viene cuando son 2+; `eleccion` es 'manual', 'habilitado' o null.
 */
export function emparejarRenglon(renglon, indices, usados = new Set(), elegidoId = null, reservados = new Map()) {
  const { porCodigo, porPalabras } = indices

  let candidatos = porCodigo.get(String(Number(renglon.codigo))) || []
  let criterio = 'Código'
  if (!candidatos.length) {
    candidatos = porPalabras.get(clavePalabras(renglon.nombreArchivo)) || []
    criterio = 'Nombre'
  }

  if (!candidatos.length) {
    return {
      empleado: null, criterio: null, candidatos: [], eleccion: null,
      error: `No existe un empleado con código ${renglon.codigo} ni con el nombre "${renglon.nombreArchivo}"`,
    }
  }

  const tomadoPorOtro = (e) => {
    const id = String(e._id)
    if (usados.has(id)) return true
    const fila = reservados.get(id)
    return fila !== undefined && fila !== renglon.filaExcel
  }
  const errorTomado = `Otro renglón del archivo ya apunta al mismo empleado (${renglon.codigo})`

  if (candidatos.length === 1) {
    const empleado = candidatos[0]
    if (tomadoPorOtro(empleado)) {
      return { empleado: null, criterio: null, candidatos: [], eleccion: null, error: errorTomado }
    }
    return { empleado, criterio, candidatos: [], eleccion: null, error: null }
  }

  // ── Varios candidatos ──────────────────────────────────────────────────────
  const lista = candidatos.map(resumenCandidato)

  const elegido = elegidoId ? candidatos.find((e) => String(e._id) === String(elegidoId)) : null
  if (elegido) {
    // Una elección manual solo choca con un renglón anterior que ya lo tomó.
    if (usados.has(String(elegido._id))) {
      return { empleado: null, criterio, candidatos: lista, eleccion: 'manual', error: errorTomado }
    }
    return { empleado: elegido, criterio, candidatos: lista, eleccion: 'manual', error: null }
  }

  const habilitados = candidatos.filter((e) => e.estado === 1 && !tomadoPorOtro(e))
  if (habilitados.length === 1) {
    return { empleado: habilitados[0], criterio, candidatos: lista, eleccion: 'habilitado', error: null }
  }

  const que = criterio === 'Código'
    ? `el código ${renglon.codigo}`
    : `el nombre "${renglon.nombreArchivo}"`
  return {
    empleado: null, criterio, candidatos: lista, eleccion: null,
    error: `Hay ${candidatos.length} empleados con ${que}${habilitados.length > 1 ? ', varios habilitados' : ' y ninguno habilitado disponible'}: elige cuál`,
  }
}

// ─── Validación contra los totales de control ────────────────────────────────

/**
 * Suma lo mapeado por tipo y lo compara contra las columnas *TOTAL* del archivo.
 * Un descuadre significa casi siempre que quedó una columna sin mapear o mapeada
 * al tipo equivocado — se avisa antes de importar, no después.
 */
export function validarTotales(renglon, mapeo) {
  let percepciones = 0
  let deducciones = 0
  for (const col of mapeo) {
    if (col.ignorar || !col.conceptoNominaId) continue
    const importe = renglon.importes[col.indice]
    if (!importe) continue
    if (col.tipo === 1) percepciones += importe
    else if (col.tipo === 2) deducciones += importe
  }
  percepciones = redondear2(percepciones)
  deducciones = redondear2(deducciones)

  const difPercepciones = renglon.totalPercepcionesArchivo === null
    ? null
    : redondear2(percepciones - renglon.totalPercepcionesArchivo)
  const difDeducciones = renglon.totalDeduccionesArchivo === null
    ? null
    : redondear2(deducciones - renglon.totalDeduccionesArchivo)

  return {
    percepciones,
    deducciones,
    neto: redondear2(percepciones - deducciones),
    difPercepciones,
    difDeducciones,
    cuadra:
      (difPercepciones === null || Math.abs(difPercepciones) < TOLERANCIA_CENTAVOS) &&
      (difDeducciones === null || Math.abs(difDeducciones) < TOLERANCIA_CENTAVOS),
  }
}

// ─── Orquestador ─────────────────────────────────────────────────────────────

/**
 * De la matriz cruda de una hoja al payload que espera
 * prenomina/importarNominaHistorica, más todo lo que la pantalla necesita para
 * la previsualización.
 *
 * @param filas          matriz de la hoja (XLSX.utils.sheet_to_json header:1)
 * @param conceptosNomina catálogo de la empresa
 * @param empleados       empleados de la empresa (incluidos los dados de baja)
 * @param mapeoGuardado   configuracionNomina.mapeoNominaHistorica
 * @param mapeoManual     mapeo ya editado por el usuario; si viene, gana sobre el automático
 * @param elecciones      { [filaExcel]: empleadoId } — empleado elegido a mano en los
 *                        renglones con varios candidatos
 */
export function analizarHoja({ filas, conceptosNomina, empleados, mapeoGuardado, mapeoManual, elecciones = {} }) {
  const encabezado = detectarEncabezado(filas)
  if (!encabezado) {
    return {
      error: 'No se encontró el encabezado de la lista de raya: hace falta un renglón con las columnas "Código" y "Empleado".',
    }
  }

  const cabecera = leerCabecera(filas, encabezado.renglon)
  const { columnas, renglones } = extraerRenglones(filas, encabezado)

  const mapeo = mapeoManual && mapeoManual.length
    ? columnas.map((col) => {
        const m = mapeoManual.find((x) => x.indice === col.indice)
        return m ? { ...col, conceptoNominaId: m.conceptoNominaId, ignorar: m.ignorar, origen: 'manual' } : col
      })
    : automapear(columnas, conceptosNomina, mapeoGuardado)

  const indices = construirIndicesEmpleadosNomina(empleados)
  const usados = new Set()

  // Si dos renglones eligieron al mismo empleado, la reserva es del primero.
  const reservados = new Map()
  for (const [fila, id] of Object.entries(elecciones || {})) {
    if (id && !reservados.has(String(id))) reservados.set(String(id), Number(fila))
  }

  const preview = renglones.map((renglon) => {
    const { empleado, criterio, error, candidatos, eleccion } = emparejarRenglon(
      renglon, indices, usados, (elecciones || {})[renglon.filaExcel], reservados
    )
    if (empleado) usados.add(String(empleado._id))
    return {
      ...renglon,
      empleadoId: empleado ? String(empleado._id) : null,
      empleadoNombre: empleado ? nombreEmpleado(empleado) : '',
      candidatos,
      eleccion,
      // La baja / deshabilitado del empleado es `estado: 2` (0 no existe en el modelo),
      // así que esta etiqueta nunca se pintaba y el renglón de un empleado que ya no
      // está en la empresa se veía igual que el de uno activo.
      empleadoBaja: empleado ? empleado.estado !== 1 : false,
      criterio,
      error,
      totales: validarTotales(renglon, mapeo),
    }
  })

  const columnasSinMapear = mapeo.filter((c) => !c.ignorar && !c.conceptoNominaId)
  const conceptosIds = [...new Set(
    mapeo.filter((c) => !c.ignorar && c.conceptoNominaId).map((c) => c.conceptoNominaId)
  )]

  const filasPayload = preview
    .filter((p) => p.empleadoId && !p.error)
    .map((p) => ({
      empleadoId: p.empleadoId,
      conceptos: mapeo
        .filter((c) => !c.ignorar && c.conceptoNominaId && p.importes[c.indice])
        .map((c) => ({ conceptoNominaId: c.conceptoNominaId, importe: p.importes[c.indice] })),
    }))

  return {
    error: null,
    encabezado,
    cabecera,
    mapeo,
    preview,
    conceptosIds,
    filas: filasPayload,
    resumen: {
      renglones: preview.length,
      emparejados: preview.filter((p) => p.empleadoId && !p.error).length,
      conError: preview.filter((p) => p.error).length,
      // Subconjunto de conError: tienen varios candidatos y falta elegir.
      porElegir: preview.filter((p) => p.error && p.candidatos.length > 1).length,
      descuadrados: preview.filter((p) => !p.totales.cuadra).length,
      columnasSinMapear: columnasSinMapear.length,
    },
  }
}
